import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { evaluatePassword } from "../lib/passwordPolicy";
import PasswordRules from "../components/auth/PasswordRules";
import SignupCheckEmail from "../components/auth/SignupCheckEmail";
import {
  SIGNUP_COOLDOWN_KEY,
  buildOnboardingMetadata,
  signupConfirmRedirectUrl,
} from "../lib/signupOnboarding";
import { startCooldown } from "../lib/resendCooldown";
import { reportError } from "../lib/errorReporter";

export default function Register() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);
  const inFlight = useRef(false);

  async function submit(e) {
    e.preventDefault();
    if (inFlight.current || busy) return;
    // Phase 2D.2A — registration enforces exactly the same password policy as
    // the password-reset screen, so the user never sees a raw provider error.
    if (!evaluatePassword(password).valid) {
      setError(t("password.error_policy"));
      return;
    }
    inFlight.current = true;
    setBusy(true); setError("");
    // Phase 2D.2B — the organization is NOT created here. The account stays
    // unverified until the user opens the confirmation link, so the two
    // non-sensitive onboarding fields travel in Supabase Auth user metadata and
    // /auth/confirm-signup completes the bootstrap once the address is verified.
    const su = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: signupConfirmRedirectUrl(),
        data: buildOnboardingMetadata({
          fullName: fullName || email,
          organizationName: orgName,
        }),
      },
    });
    inFlight.current = false;
    setBusy(false);
    if (su.error) {
      // The raw provider error is never rendered and never logged.
      reportError("auth.signup_request");
      const limited = su.error.status === 429 || su.error.code === "over_email_send_rate_limit";
      setError(limited ? t("signup.error_rate_limited") : t("signup.error_generic"));
      return;
    }
    startCooldown(SIGNUP_COOLDOWN_KEY);
    setRequested(true);
  }

  return (
    <div className="qw-auth-wrap">
      <div className="qw-auth-side">
        <div>
          <div className="brand">OFFERTRACE</div>
          <div className="head">{t("common.tagline")}</div>
          <div className="sub">{t("auth.signup_note")}</div>
        </div>
        <div style={{ opacity: .7, fontSize: 12 }}>© OfferTrace</div>
      </div>
      <div className="qw-auth-panel">
        {requested ? (
          <SignupCheckEmail email={email.trim()} />
        ) : (
        <form className="qw-auth-form" onSubmit={submit}>
          <h1>{t("auth.create_your_account")}</h1>
          <p className="lead">{t("auth.setup_org")}</p>
          <div className="qw-field">
            <label>{t("auth.full_name")}</label>
            <input className="qw-input" required value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="register-name" />
          </div>
          <div className="qw-field">
            <label>{t("auth.organization_name")}</label>
            <input className="qw-input" required value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="register-org" />
          </div>
          <div className="qw-field">
            <label>{t("auth.email")}</label>
            <input className="qw-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" autoComplete="email" />
          </div>
          <div className="qw-field">
            <label>{t("auth.password")}</label>
            <input className="qw-input" type="password" required minLength={8} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} data-testid="register-password" autoComplete="new-password" />
          </div>
          <PasswordRules value={password} testIdPrefix="register" />
          {error && <div className="qw-error" data-testid="register-error">{error}</div>}
          <button className="qw-btn primary stretch" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={busy} data-testid="register-submit">
            {busy ? t("common.loading") : t("auth.create_account")}
          </button>
          <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
            {t("auth.have_account")} <Link to="/login" data-testid="go-login" style={{ color: "#0f766e", fontWeight: 600 }}>{t("auth.sign_in")}</Link>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
